import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { XIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import { brainsEndpoints } from '@/api/endpoints';
import {
  SidebarWrapper,
  SidebarWrapperTitle,
  SidebarWrapperForm,
  SidebarWrapperContent,
  SidebarWrapperFooter,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useBrains from '@/hooks/useBrains';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { setBrainId } from '@/redux/session/actions';
import { selectBrainId } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { hasDirtyFields } from '@/util/util';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

type AIAgentFormValues = { name?: string };

/**
 * AI Agent form component for sidebar-based renaming.
 */
export const AIAgentForm = () => {
  const brainId = useSelector(selectBrainId);
  const { brain, updateBrain, updateStatus } = useBrains(brainId);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState(false);

  // Create schema with excludeName to allow saving without name change
  const brainSchema = yup
    .object()
    .shape({
      name: createNameFieldSchema({
        getEndpoint: brainsEndpoints.checkName,
        excludeName: brain?.name,
      }),
    });

  const form = useForm<AIAgentFormValues>({
    resolver: yupResolver<AIAgentFormValues, unknown, AIAgentFormValues>(brainSchema),
    defaultValues: { name: brain?.name || '' },
    mode: 'onChange', // Show validation errors while typing
  });

  const { reset, formState } = form;

  // Update form when brain data changes and set focus
  useEffect(() => {
    if (brain) {
      reset({ name: brain.name });

      // Set focus with delay to ensure DOM is ready
      const timer = setTimeout(() => {
        form.setFocus('name', { shouldSelect: true });
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [brain, reset, form]);

  useEscapeKey(() => {
    const hasUnsavedChanges = hasDirtyFields(form);

    if (hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      dispatch(setSidebarContent(null));
    }
  });

  const onSubmit = (values: AIAgentFormValues) => {
    if (!brain) {
      return;
    }

    if (!hasDirtyFields(form)) {
      form.setFocus('name', { shouldSelect: true });
      return;
    }

    updateBrain({ brain_id: brain.brain_id, name: values.name || '' });
    dispatch(setSidebarContent(null));
    dispatch(setBrainId({ brainId: null }));
  };

  const handleClose = () => {
    const hasUnsavedChanges = hasDirtyFields(form);

    if (hasUnsavedChanges) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      dispatch(setSidebarContent(null));
    }

    dispatch(setBrainId({ brainId: null }));
  };

  const handleDiscard = () => {
    setIsUnsavedChangesModalOpen(false);
    dispatch(setSidebarContent(null));
  };

  const handleGoBack = () => {
    setIsUnsavedChangesModalOpen(false);
  };

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">{t('brains.rename_ai_agent')}</h2>
        <Button variant="ghost-neutral" size="icon-sm" type="button" onClick={handleClose}>
          <XIcon size={16} />
        </Button>
      </SidebarWrapperTitle>

      <Form {...form}>
        <SidebarWrapperForm onSubmit={form.handleSubmit(onSubmit)}>
          <SidebarWrapperContent className="px-1 pb-2">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('brains.ai_agent_name')}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t('common.name_placeholder')} />
                  </FormControl>
                  <FormDescription>{t('brains.ai_agent_name_helper')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </SidebarWrapperContent>

          <SidebarWrapperFooter>
            <Button
              type="submit"
              disabled={formState.isSubmitting}
              isLoading={updateStatus === 'pending'}
            >
              {updateStatus === 'pending' ? t('common.saving') : t('common.rename')}
            </Button>

            <Button type="button" variant="ghost-neutral" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
          </SidebarWrapperFooter>
        </SidebarWrapperForm>
      </Form>

      {/* Unsaved changes confirmation modal */}
      {isUnsavedChangesModalOpen && (
        <UnsavedChangesModal
          onDiscard={handleDiscard}
          onGoBack={handleGoBack}
          onOpenChange={setIsUnsavedChangesModalOpen}
        />
      )}
    </SidebarWrapper>
  );
};
